import { Sheet } from "@themes/pollinator/ui/Sheet";
import { Type } from "@themes/pollinator/ui/Type";
import { marketingType } from "../../marketing.type";

export type Props = {
  isOpen: boolean;
  onClose: () => void;
  organizations: { name: string; location?: string | null }[];
  /** How many organizations the network counts, as it should be printed */
  organizationCount: string;
};

/**
 * Every partner organization, in a panel that slides over the page.
 *
 * The page previews a couple of dozen and says the rest are "here"; this is
 * the here. A panel rather than a page of its own, so somebody checking for
 * their organization stays where they were reading and can carry straight on.
 */
export function MarketingPartnerOrganizationsSheet({
  isOpen,
  onClose,
  organizations,
  organizationCount,
}: Props) {
  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      aria-labelledby="partner-organizations-heading"
      data-testid="partner-organizations-sheet"
      className="max-w-[440px] gap-0 justify-start border-l border-brand-800 text-brand-800 overflow-y-auto"
    >
      <div className="flex flex-col gap-6 p-8 border-b border-brand-800">
        <Type
          variant="h3"
          as="h2"
          id="partner-organizations-heading"
          className={`${marketingType.display} ${marketingType.displayMedium}`}
        >
          Pollinator’s Partner Organizations
        </Type>
        <Type variant="body2" className="text-[16px] md:text-[16px] leading-[24px] max-w-none">
          We’ve already partnered with {organizationCount}+ residencies, fellowships, and
          nonprofit organizations so that their alumni, staff, and leadership
          can join as Pollinator participants, and become eligible for our
          peer-coaching program and funding opportunities.
        </Type>
      </div>
      {/* The list can take focus, so that it can be scrolled from the
          keyboard: the keys scroll whatever holds focus, and without this the
          only thing in the panel that could hold it was the close button. */}
      <ul
        className="flex flex-col gap-4 p-8 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-600"
        tabIndex={0}
        aria-label="Partner organizations"
      >
        {organizations.map(({ name, location }, index) => (
          <Type
            variant="body2"
            as="li"
            key={`${name}-${index}`}
            className={`${marketingType.small} max-w-none`}
          >
            <span className="font-bold">{name}</span>
            {location && <span className="uppercase pl-2">{location}</span>}
          </Type>
        ))}
      </ul>
    </Sheet>
  );
}

export default MarketingPartnerOrganizationsSheet;
